import type { ActiveDiscountOffer, DiscountOfferStatus } from '@ophelos/shared/types/discount-offer';
import { isOfferActive } from '@ophelos/shared/utils/discount-offer';
import { getActiveDiscountOfferFromConnect } from '@/lib/connect-api';

export type {
  ActiveDiscountOffer,
  DiscountConfigSnapshot,
  DiscountOfferStatus,
  DiscountOptionSnapshot,
} from '@ophelos/shared/types/discount-offer';
export { type DiscountOptionKey, discountedAmountMillicents, isOfferActive } from '@ophelos/shared/utils/discount-offer';

/**
 * Fetches the active discount offer for a case via Connect API.
 * Returns the deterministic winner (highest pay_now percentage) or null if no eligible offer exists.
 *
 * Failures are swallowed and returned as null: discount display is non-critical
 * UX and must never break the page.
 */
export async function getActiveDiscountOffer(
  caseId: string,
  authStage: 'limited' | 'full' | null,
  getAccessToken?: () => Promise<string | null>,
): Promise<ActiveDiscountOffer | null> {
  try {
    const row = await getActiveDiscountOfferFromConnect(caseId, authStage, getAccessToken);
    if (!row || !row.offer_id) return null;
    return {
      offerId: row.offer_id,
      campaignId: row.campaign_id,
      caseId: row.case_id,
      status: row.status as DiscountOfferStatus,
      configSnapshot: row.discount_config_snapshot as ActiveDiscountOffer['configSnapshot'],
      expiresAt: row.expires_at ?? null,
    };
  } catch (err) {
    console.warn('[getActiveDiscountOffer] non-fatal failure', err);
    return null;
  }
}

/**
 * Are all the offers a plan was priced against still live?
 *
 * A plan configured under a discount is only safe to create while that pricing holds. The review
 * screen sits between pricing and authorisation for as long as the customer leaves it open, and
 * `DebtDataContext` refetches offers the moment the soonest expiry passes — so the figures the
 * plan was built from can lapse underneath it. Nothing server-side checks that a schedule covers
 * the balance, so authorising on lapsed pricing would create an underfunded plan.
 *
 * Offers are keyed by case id, so the lookup is by value: account scope prices against one offer
 * per covered case, and any one of them lapsing invalidates the total.
 *
 * @param pricedOfferIds Offers the plan total was computed from; absent or empty when no
 *   discount applied, in which case there is no pricing to lapse.
 */
export function pricedOffersStillLive(
  pricedOfferIds: string[] | undefined,
  activeOffers: Record<string, ActiveDiscountOffer>,
): boolean {
  if (!pricedOfferIds?.length) return true;
  // Wrapped, not point-free: `filter` would pass the index as `isOfferActive`'s `now`.
  const live = Object.values(activeOffers).filter((o) => isOfferActive(o));
  return pricedOfferIds.every((id) => live.some((o) => o.offerId === id));
}
